import { useDisclosure } from '@dwarvesf/react-hooks';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Input,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { CollapsibleSection } from 'components/common/CollapsibleSection';
import { CUSTOM_RANGE_LABEL } from 'components/common/DatePicker/DateRangePicker';
import { typography } from 'components/common/Typography/styles';
import { IconBoldAddCircle } from 'components/icons/components/bold/IconBoldAddCircle';
import { IconBoldChart1 } from 'components/icons/components/bold/IconBoldChart1';
import { IconBoldCloseCircle } from 'components/icons/components/bold/IconBoldCloseCircle';
import { IconLinearDiagram } from 'components/icons/components/linear/IconLinearDiagram';
import { IconLinearMonitorMobile } from 'components/icons/components/linear/IconLinearMonitorMobile';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import { IconOutlineCalendar1 } from 'components/icons/components/outline/IconOutlineCalendar1';
import { IconOutlineSearchNormal1 } from 'components/icons/components/outline/IconOutlineSearchNormal1';
import { CreatorAvatar } from 'features/creator';
import {
  NestedFiltersMenuItemBaseValueType,
  NestedFiltersMenuView,
} from 'features/nestedFilters';
import { generateDateRangeOptions } from 'features/socialListeningCreatorProfile';
import { platformIconMap, platformLabelMap } from 'features/socialPost';
import {
  CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment,
  Platform,
} from 'graphql/generated';
import { useLocalStorage } from 'hooks/localStorage/useLocalStorage';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import * as yup from 'yup';
import { BrandCompetitorsShareOfVoiceBarChart } from './BrandCompetitorsShareOfVoiceBarChart';
import { BrandCompetitorsShareOfVoiceLineChart } from './BrandCompetitorsShareOfVoiceLineChart';
import { BrandCompetitorsShareOfVoiceSocialPostsDialog } from './BrandCompetitorsShareOfVoiceSocialPostsDialog';
import { BrandCompetitorsShareOfVoiceTable } from './BrandCompetitorsShareOfVoiceTable';

export const SHARE_OF_VOICE_COLORS = [
  theme.colors?.utility['purple-2'],
  theme.colors?.utility['blue-4'],
  theme.colors?.utility['orange-2'],
  theme.colors?.utility['purple-3'],
  theme.colors?.utility['yellow-2'],
  theme.colors?.utility['green-2'],
  theme.colors?.utility['teal-2'],
  theme.colors?.utility['pink-2'],
  theme.colors?.utility['blue-3'],
  theme.colors?.utility['teal-3'],
  theme.colors?.utility['yellow-3'],
  theme.colors?.utility['green-3'],
  theme.colors?.utility['orange-3'],
  theme.colors?.utility['pink-3'],
];

export enum AggregationType {
  ShareOfVoice = 'shareOfVoice',
  MentionCount = 'mentionCount',
  ViewCount = 'viewCount',
  TotalEngagement = 'totalEngagement',
  EngagementRate = 'engagementRate',
}

export const AggregationTypeLabelMap = {
  [AggregationType.ShareOfVoice]: 'Share of Voice',
  [AggregationType.MentionCount]: 'Mention Count',
  [AggregationType.ViewCount]: 'View Count',
  [AggregationType.EngagementRate]: 'Engagement Rate',
  [AggregationType.TotalEngagement]: 'Total Engagement',
};

const aggregationTypeOptions = Object.values(AggregationType).map((type) => ({
  label: AggregationTypeLabelMap[type],
  value: type,
}));

const BRAND_COMPETITORS_SHARE_OF_VOICE_FILTERS =
  'brand-competitors-share-of-voice-filters';

export type Filters = {
  aggregationType: NestedFiltersMenuItemBaseValueType<{
    label: string;
    value: AggregationType;
  }>;
  platform: NestedFiltersMenuItemBaseValueType<{
    label: string;
    value: Platform;
  }>;
  dateRange: NestedFiltersMenuItemBaseValueType<{
    label: string;
    startDate: Date;
    endDate: Date;
  }>;
};

export const BrandCompetitorsShareOfVoiceView = () => {
  const { brandId = '' } = useParams();

  const dateRangeOptions = useMemo(() => generateDateRangeOptions(), []);

  const { value: filters, setValue: setFilters } = useLocalStorage<Filters>(
    BRAND_COMPETITORS_SHARE_OF_VOICE_FILTERS + brandId,
    {
      aggregationType: {
        value: aggregationTypeOptions[0],
      },
      platform: {
        value: {
          label: platformLabelMap[Platform.Instagram],
          value: Platform.Instagram,
        },
      },
      dateRange: {
        value: dateRangeOptions[0],
      },
    },
    yup
      .object()
      .shape({
        aggregationType: yup
          .object()
          .shape({
            value: yup
              .object()
              .shape({
                label: yup.string().required(),
                value: yup.string().required(),
              })
              .required(),
          })
          .required(),
        platform: yup
          .object()
          .shape({
            value: yup
              .object()
              .shape({
                label: yup.string().required(),
                value: yup.string().required(),
              })
              .required(),
          })
          .required(),
        dateRange: yup
          .object()
          .shape({
            value: yup
              .object()
              .shape({
                label: yup.string().required(),
                startDate: yup.date().required(),
                endDate: yup.date().required(),
              })
              .required(),
          })
          .required(),
      })
      .noUnknown(),
  );

  const selectedDateRangeOption = dateRangeOptions.find(
    (option) => filters.dateRange.value?.label === option.label,
  );
  const selectedDateRangeLabel = useMemo(() => {
    if (filters.dateRange.value?.label === CUSTOM_RANGE_LABEL) {
      return CUSTOM_RANGE_LABEL;
    }

    return selectedDateRangeOption?.label;
  }, [filters.dateRange.value?.label, selectedDateRangeOption]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = debounce((value) => setSearchTerm(value), 300);

  // Manage selected items for displaying in the charts
  const [items, setItems] = useState<
    CompetitorShareOfVoiceAggregatedDataItemFragmentBcShareOfVoiceTableFragment[]
  >([]);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  const selectedItems = useMemo(() => {
    return items.filter((item) => selectedItemIds.includes(item.id));
  }, [items, selectedItemIds]);

  // Manage chart type
  const [chartType, setChartType] = useState<'bar' | 'line'>('bar');
  const [chartActiveItemId, setChartActiveItemId] = useState<
    string | undefined
  >();

  // Build a map of item id <> color based on the index
  const itemIdToColorMap = items.reduce((acc, item, index) => {
    acc[item.id] =
      SHARE_OF_VOICE_COLORS[index % SHARE_OF_VOICE_COLORS.length] || '';

    return acc;
  }, {} as Record<string, string>);

  // Manage which competitor is open in the social posts dialog
  const {
    isOpen: isSocialPostsDialogOpen,
    onOpen: _openSocialPostsDialog,
    onClose: closeSocialPostsDialog,
  } = useDisclosure();

  const [
    competitorTrackingBrandIdForSocialPostsDialog,
    setCompetitorTrackingBrandIdForSocialPostsDialog,
  ] = useState<string | undefined>();
  const [dateRangeForSocialPostsDialog, setDateRangeForSocialPostsDialog] =
    useState<
      | {
          startDate: Date;
          endDate: Date;
        }
      | undefined
    >();

  const openSocialPostsDialog = (
    competitorTrackingBrandId: string,
    dateRange?: {
      startDate: Date;
      endDate: Date;
    },
  ) => {
    setCompetitorTrackingBrandIdForSocialPostsDialog(competitorTrackingBrandId);
    setDateRangeForSocialPostsDialog(dateRange);
    _openSocialPostsDialog();
  };

  const competitorTrackingBrandForSocialPostsDialog = useMemo(() => {
    return items.find(
      (item) =>
        item.base.brand.id === competitorTrackingBrandIdForSocialPostsDialog,
    )?.base.brand;
  }, [items, competitorTrackingBrandIdForSocialPostsDialog]);

  // TODO: Double check if we can trim this. I implemented this as a quick way to
  // provide sth like an "instant load" UX, but I'm not sure
  const initialAggregatedDataForSocialPostsDialog = useMemo(() => {
    // If the date range is custom, we wouldn't know the initial aggregated data
    if (dateRangeForSocialPostsDialog) {
      return undefined;
    }

    return items.find(
      (item) => item.id === competitorTrackingBrandIdForSocialPostsDialog,
    )?.aggregatedData;
  }, [
    items,
    competitorTrackingBrandIdForSocialPostsDialog,
    dateRangeForSocialPostsDialog,
  ]);

  return (
    <>
      <Box
        sx={{
          mx: 8,
          borderRadius: 3,
          boxShadow: '0px 4px 14px 0px #0000000D',
          border: `1px solid #2306030D`,
        }}
      >
        <Box
          sx={{
            py: 6,
            px: 8,
            borderBottom: `1px solid #2306030D`,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="headline-xl" fontSize={24}>
              Share of Voice
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                alignItems: 'center',
              }}
            >
              <Tabs
                value={chartType}
                onChange={(_, value) => setChartType(value)}
                sx={{
                  minHeight: 0,
                  p: 1,
                  borderRadius: 2,
                  bgcolor: theme.colors?.utility[275],

                  '& .MuiTabs-flexContainer': {
                    gap: 0.5,
                  },

                  '& .MuiTab-root': {
                    padding: 1.5,
                    minWidth: 0,
                    borderRadius: 2,
                    minHeight: 0,
                    color: theme.colors?.primary.black,

                    '&.Mui-selected': {
                      color: theme.colors?.primary.white,
                      backgroundColor: theme.colors?.primary.black,
                    },
                  },

                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                }}
              >
                <Tab value="bar" label={<IconBoldChart1 size={16} />} />
                <Tab value="line" label={<IconLinearDiagram size={16} />} />
              </Tabs>
              <NestedFiltersMenuView
                items={[
                  {
                    type: 'single-select',
                    key: 'aggregationType',
                    label: 'Sort by',
                    options: aggregationTypeOptions,
                  },
                ]}
                values={{
                  aggregationType: filters.aggregationType,
                }}
                onChange={(values) => {
                  setFilters((o) => ({
                    ...o,
                    aggregationType: values.aggregationType,
                  }));
                }}
                componentsProps={{
                  trigger: {
                    text:
                      filters.aggregationType?.value?.label ||
                      'Aggregation Type',
                    Icon: IconLinearSort,
                  },
                }}
              />
              <NestedFiltersMenuView
                items={[
                  {
                    type: 'single-select',
                    key: 'platform',
                    options: [
                      Platform.Instagram,
                      Platform.Tiktok,
                      Platform.Youtube,
                    ].map((platform) => ({
                      label: platformLabelMap[platform],
                      value: platform,
                    })),
                  },
                ]}
                values={{
                  platform: filters.platform,
                }}
                onChange={(values) => {
                  setFilters((o) => ({
                    ...o,
                    platform: values.platform,
                  }));
                }}
                componentsProps={{
                  trigger: {
                    text: filters.platform?.value?.label || 'Platforms',
                    Icon: filters.platform.value?.value
                      ? platformIconMap[filters.platform.value?.value]
                      : IconLinearMonitorMobile,
                  },
                }}
              />
              <NestedFiltersMenuView
                items={[
                  {
                    type: 'date-range',
                    key: 'dateRange',
                    options: dateRangeOptions,
                  },
                ]}
                values={{
                  dateRange: filters.dateRange,
                }}
                onChange={(values) => {
                  setFilters((o) => ({
                    ...o,
                    dateRange: values.dateRange,
                  }));
                }}
                componentsProps={{
                  trigger: {
                    text: selectedDateRangeLabel || 'Date Range',
                    Icon: IconOutlineCalendar1,
                  },
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {selectedItems.map((item) => {
              const color = itemIdToColorMap[item.id];
              const creatorWithProfilePicture = item.base.brand.creators.find(
                (creator) => creator.profilePictureUrl,
              );

              return (
                <Chip
                  key={item.id}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: 6,
                          bgcolor: color,
                        }}
                      />
                      {creatorWithProfilePicture && (
                        <CreatorAvatar
                          creator={creatorWithProfilePicture}
                          size={24}
                          hidePlatformIcon
                        />
                      )}
                      <Typography variant="headline-sm">
                        {item.base.brand.name}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    color,
                    position: 'relative',
                    border: 'none',
                    ...theme.typography['headline-sm'],

                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: color,
                      borderRadius: 10,
                      opacity: chartActiveItemId === item.id ? 0.4 : 0.2,
                      pointerEvents: 'none',
                    },
                  }}
                  deleteIcon={
                    <IconBoldCloseCircle
                      size={16}
                      style={{ color, marginRight: theme.spacing(2) }}
                    />
                  }
                  onDelete={() => {
                    setSelectedItemIds(
                      selectedItemIds.filter((id) => id !== item.id),
                    );
                  }}
                  onMouseEnter={() => {
                    setChartActiveItemId(item.id);
                  }}
                  onMouseLeave={() => {
                    setChartActiveItemId(undefined);
                  }}
                />
              );
            })}
            <NestedFiltersMenuView
              items={[
                {
                  type: 'multi-select',
                  key: 'selectedItems',
                  options: items.map((item) => ({
                    label: item.base.brand.name,
                    value: item.id,
                  })),
                },
                {
                  type: 'divider',
                },
                {
                  type: 'custom',
                  key: 'aggregationType',
                  render: () => {
                    return (
                      // @ts-ignore -- `to` will not be recognized by MUI Button, but it works
                      <Button
                        LinkComponent={Link}
                        startIcon={<IconBoldAddCircle size={24} />}
                        size="small"
                        target="_blank"
                        to={PlotRoutes.socialListeningOnboarding(
                          'competitors',
                          brandId,
                        )}
                      >
                        <Typography variant="subhead-lg">
                          New competitor
                        </Typography>
                      </Button>
                    );
                  },
                },
              ]}
              values={{
                selectedItems: {
                  value: selectedItems.map((item) => ({
                    label: item.base.brand.name,
                    value: item.id,
                  })),
                },
              }}
              onChange={(values) => {
                setSelectedItemIds(
                  (
                    (values.selectedItems?.value as {
                      label: string;
                      value: string;
                    }[]) ?? []
                  ).map((item) => item.value),
                );
              }}
              renderTrigger={(props) => {
                return (
                  <IconButton
                    ref={props.anchorElRef}
                    onClick={props.onOpen}
                    size="small"
                    sx={{
                      p: 0,
                    }}
                  >
                    <IconBoldAddCircle
                      size={24}
                      style={{
                        color: theme.colors?.primary.black,
                      }}
                    />
                  </IconButton>
                );
              }}
            />
          </Box>
          <Box>
            {chartType === 'bar' && (
              <BrandCompetitorsShareOfVoiceBarChart
                aggregationType={
                  filters.aggregationType?.value?.value ||
                  AggregationType.ShareOfVoice
                }
                selectedItems={selectedItems}
                itemIdToColorMap={itemIdToColorMap}
                activeItemId={chartActiveItemId}
                onCompetitorClick={openSocialPostsDialog}
              />
            )}
            {chartType === 'line' && (
              <BrandCompetitorsShareOfVoiceLineChart
                filters={{
                  brandId,
                  platforms: filters.platform.value
                    ? [filters.platform.value?.value]
                    : [],
                  dateRange: {
                    startDate: filters.dateRange.value?.startDate,
                    endDate: filters.dateRange.value?.endDate,
                  },
                }}
                aggregationType={
                  filters.aggregationType?.value?.value ||
                  AggregationType.ShareOfVoice
                }
                selectedItemIds={selectedItemIds}
                itemIdToColorMap={itemIdToColorMap}
                activeItemId={chartActiveItemId}
                onCompetitorClick={openSocialPostsDialog}
              />
            )}
          </Box>
        </Box>
        <Box
          sx={{
            py: 6,
            px: 8,
            '.MuiDivider-root': {
              display: 'none',
            },
          }}
        >
          <CollapsibleSection
            headerTitle="Competitors"
            headerFullWidth
            sx={{
              '.MuiTypography-root': {
                ...typography['headline-md'],
                fontSize: 20,
              },
            }}
            defaultIsOpen
          >
            <Box
              sx={{
                py: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                overflow: 'hidden',
              }}
            >
              <Input
                disableUnderline
                placeholder="Search"
                autoFocus
                onChange={(e) => {
                  debouncedSearchTerm(e.target.value);
                }}
                startAdornment={
                  <IconOutlineSearchNormal1
                    size={16}
                    style={{
                      marginRight: theme.spacing(2),
                      color: theme.colors?.utility[700],
                    }}
                  />
                }
                sx={{
                  borderRadius: 25,
                  background: theme.colors?.utility[275],
                  padding: theme.spacing(1, 4),
                  ...theme.typography['subhead-xl'],
                  maxWidth: 480,
                }}
              />
              <BrandCompetitorsShareOfVoiceTable
                filters={{
                  brandId,
                  platforms: filters.platform.value
                    ? [filters.platform.value?.value]
                    : [],
                  dateRange: {
                    startDate: filters.dateRange.value?.startDate,
                    endDate: filters.dateRange.value?.endDate,
                  },
                  ...(searchTerm && { q: searchTerm }),
                }}
                selectedItemIds={selectedItemIds}
                setSelectedItemIds={setSelectedItemIds}
                setItems={setItems}
                itemIdToColorMap={itemIdToColorMap}
              />
            </Box>
          </CollapsibleSection>
        </Box>
      </Box>

      {isSocialPostsDialogOpen &&
        competitorTrackingBrandForSocialPostsDialog && (
          <BrandCompetitorsShareOfVoiceSocialPostsDialog
            brandId={brandId}
            competitorTrackingBrand={
              competitorTrackingBrandForSocialPostsDialog
            }
            initialAggregatedData={initialAggregatedDataForSocialPostsDialog}
            isOpen
            filters={{
              ...filters,
              dateRange: dateRangeForSocialPostsDialog
                ? {
                    value: {
                      label: CUSTOM_RANGE_LABEL,
                      startDate: dateRangeForSocialPostsDialog.startDate,
                      endDate: dateRangeForSocialPostsDialog.endDate,
                    },
                  }
                : filters.dateRange,
            }}
            onClose={() => {
              setCompetitorTrackingBrandIdForSocialPostsDialog(undefined);
              setDateRangeForSocialPostsDialog(undefined);
              closeSocialPostsDialog();
            }}
          />
        )}
    </>
  );
};
